import { Space } from 'antd';
import { Button, Card } from 'components';
import { useAppNotifications } from 'hooks';
import { useStrapiQuery } from 'hooks/useStrapiQuery';
import { useState, type VFC } from 'react';
import { UploadButton } from './components';
import { HardwareListFilter } from './components/Filter';
import { CopyHWQueryModal } from './components/modal/CopyQuery';
import { HardwareModal } from './components/modal/Hardware';
import { HardwareListTable } from './components/Table';
import { Icon } from '@/components/icon';
import {
  GET_HARDWARE_DATA_LIST,
  type HardwaresDataItem,
  type HardwaresDataList,
  type HardwareVariables,
} from './queries/getHardwareDataList';
import { useHardwareListFilterWithPagination } from './useHardwareListFilterWithPagination.ts';

export const HardwareList: VFC = () => {
  const [isEditModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [isCopyModalVisible, setCopyModalVisible] = useState<boolean>(false);

  const appNotification = useAppNotifications();

  const [hardwareId, setHardwareId] = useState<HardwaresDataItem['id']>(undefined);
  const { filter, pagination, setPaginationState } = useHardwareListFilterWithPagination();

  const {
    data: { hardwares: { data: hardwares = [] } = {} } = {},
    loading: isLoading,
    refetch,
  } = useStrapiQuery<HardwaresDataList, HardwareVariables>(GET_HARDWARE_DATA_LIST, {
    variables: {
      name: filter.name,
      externalId: filter.externalId,
      category: filter.category,
      sort: [filter.sort],
      limit: pagination.pageSize,
      start: (pagination.current - 1) * pagination.pageSize,
    },
    onCompleted(response) {
      setPaginationState({
        total: response?.hardwares?.meta?.pagination?.total ?? 0,
      });
    },
    onError(error) {
      console.error(error);
      appNotification.error({
        message: `Error while fetching hardware list. Please try again.`,
      });
    },
    fetchPolicy: 'cache-and-network',
  });

  const handleEdit = (item: HardwaresDataItem) => {
    setHardwareId(item.id);
    setEditModalVisible(true);
  };

  const handleCopy = (item: HardwaresDataItem) => {
    setHardwareId(item.id);
    setCopyModalVisible(true);
  };

  const handleHide = () => {
    setEditModalVisible(false);
    setCopyModalVisible(false);
    setHardwareId(undefined);
  };

  const handleSuccess = () => {
    refetch();
    handleHide();
  };

  return (
    <Space className="w-100" direction="vertical">
      {isEditModalVisible && (
        <HardwareModal
          hardware={hardwares.find(hardware => hardware.id === hardwareId)}
          onCancel={handleHide}
          onSuccess={handleSuccess}
        />
      )}
      {isCopyModalVisible && (
        <CopyHWQueryModal
          hardware={hardwares.find(hardware => hardware.id === hardwareId)}
          onHide={handleHide}
          onSuccess={handleSuccess}
        />
      )}
      <Card>
        <HardwareListFilter />
      </Card>
      <Card>
        <Space className="w-100" direction="vertical" style={{ maxWidth: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space wrap>
              <Button icon={<Icon iconString="plus" />} onClick={() => setEditModalVisible(true)}>
                Add New Hardware
              </Button>
              <UploadButton onSuccess={refetch} />
            </Space>
          </div>
          <HardwareListTable
            data={hardwares}
            loading={isLoading}
            onCopy={handleCopy}
            onEdit={handleEdit}
            onSuccess={refetch}
          />
        </Space>
      </Card>
    </Space>
  );
};
